import { useState, useEffect, useMemo } from "react";
import { opApi, apiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Loader2, User } from "lucide-react";

// Phone-first customer matching to avoid duplicates.
export function matchCustomers(list, phone, email, name) {
  const p = (phone || "").replace(/\D/g, "");
  if (p.length >= 3) return (list || []).filter((c) => (c.phone || "").replace(/\D/g, "").includes(p)).slice(0, 6);
  const e = (email || "").toLowerCase().trim();
  if (e.length >= 3) return (list || []).filter((c) => (c.email || "").toLowerCase().includes(e)).slice(0, 6);
  const n = (name || "").toLowerCase().trim();
  if (n.length >= 2) return (list || []).filter((c) => (c.name || "").toLowerCase().includes(n)).slice(0, 6);
  return [];
}

export function AddCustomerDialog({ open, onOpenChange, onCreated, defaultName = "", defaultPhone = "", defaultEmail = "", customers = [] }) {
  const [phone, setPhone] = useState(defaultPhone);
  const [email, setEmail] = useState(defaultEmail);
  const [name, setName] = useState(defaultName);
  const [programs, setPrograms] = useState([]);
  const [selected, setSelected] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setPhone(defaultPhone); setEmail(defaultEmail); setName(defaultName); setSelected([]);
    opApi.get("/tenant/loyalty/programs").then((r) => setPrograms((r.data || []).filter((p) => p.active && !p.expired))).catch(() => {});
  }, [open, defaultName, defaultPhone, defaultEmail]);

  const matches = useMemo(() => matchCustomers(customers, phone, email, name), [customers, phone, email, name]);
  const hasContact = !!(phone.trim() || email.trim());
  const toggle = (id) => setSelected((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);

  const save = async () => {
    if (!hasContact) { toast.error("Add a phone or email to save a customer (or use a guest ticket)."); return; }
    if (!name.trim()) { toast.error("Name is required when a phone or email is provided (so notifications can be personalized)."); return; }
    setSaving(true);
    try {
      const { data } = await opApi.post("/tenant/customers", {
        name: name.trim(), phone: phone.trim() || null, email: email.trim() || null, program_ids: selected });
      toast.success(`${data.customer.name} saved${data.enrolled_programs.length ? ` · enrolled in ${data.enrolled_programs.length}` : ""}`);
      onCreated && onCreated(data.customer);
      onOpenChange(false);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="add-customer-dialog">
        <DialogHeader>
          <DialogTitle>Add customer</DialogTitle>
          <DialogDescription>Start with a phone number — we'll match existing customers to avoid duplicates.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Phone</Label><Input autoFocus className="rounded-none mt-1" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="555 123 4567" data-testid="add-customer-phone" /></div>
            <div><Label className="text-xs">Email <span className="text-muted-foreground normal-case">(optional)</span></Label><Input className="rounded-none mt-1" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="optional" data-testid="add-customer-email" /></div>
          </div>

          {matches.length > 0 && (
            <div className="border border-amber-300 bg-amber-50" data-testid="add-customer-matches">
              <div className="px-3 py-1.5 text-[11px] uppercase tracking-wider text-amber-700 border-b border-amber-200">Existing matches</div>
              {matches.map((c) => (
                <button key={c.id} className="w-full flex items-center gap-2 px-3 py-2 hover:bg-amber-100 text-left"
                  onClick={() => { onCreated && onCreated(c); onOpenChange(false); }} data-testid={`add-customer-match-${c.id}`}>
                  <User className="h-4 w-4 text-amber-700 shrink-0" />
                  <span className="text-sm font-medium">{c.name}</span>
                  <span className="text-xs text-muted-foreground ml-auto">{c.phone || c.email}</span>
                </button>
              ))}
            </div>
          )}

          <div><Label className="text-xs">Name {hasContact && <span className="text-destructive">*</span>}<span className="text-muted-foreground normal-case"> {hasContact ? "(needed for notifications)" : "(optional)"}</span></Label>
            <Input className="rounded-none mt-1" value={name} onChange={(e) => setName(e.target.value)} data-testid="add-customer-name" /></div>

          {programs.length > 0 && (
            <div>
              <Label className="text-xs">Enroll in loyalty</Label>
              <div className="mt-1.5 space-y-1.5 max-h-40 overflow-auto border border-border p-2">
                {programs.map((p) => (
                  <label key={p.id} className="flex items-center gap-2 text-sm cursor-pointer" data-testid={`add-customer-program-${p.id}`}>
                    <Checkbox checked={selected.includes(p.id)} onCheckedChange={() => toggle(p.id)} />
                    <span>{p.title}</span>
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-none" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button className="rounded-none" onClick={save} disabled={saving} data-testid="add-customer-save">
            {saving && <Loader2 className="h-4 w-4 animate-spin mr-1" />} Save customer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
